import type { NavigationParams } from '@/modules/Project/Navigation/navigationTypes'
import { invariant } from '@/shared/utils/typeAssertions'
import { inject, provide, type Ref } from 'vue'

/**
 * Defines the bare minimum any navigation context must provide
 * in order to be provided under the navigationContextToken key
 */
export type NavigationContext = {
  breadcrumbs: Ref<NavigationParams[]>
  navigate: (params: NavigationParams) => void
  reset: () => void
}

export const navigationContextToken = Symbol('navigationContext')

/**
 * Injects any provided navigation context optionally or as a mandatory key.
 * To reuse type definitions for inject/provide, a type that extends NavigationContext
 * can be declared.
 */
export function useNavigationContext<T extends NavigationContext>(props: { required: true }): T
export function useNavigationContext<T extends NavigationContext>(props?: {
  required?: boolean
}): T | null
export function useNavigationContext<T extends NavigationContext>(props?: {
  required?: boolean
}): T | null {
  const ctx = inject(navigationContextToken, null)
  if (props?.required) {
    invariant(ctx, 'Missing context › `provideNavigationContext()`')
  }

  return ctx
}

/**
 * Can provide the base or any custom navigation context, as long as it
 * implements the NavigationContext type at minimum.
 */
export function provideNavigationContext<T extends NavigationContext>(ctx: T) {
  provide(navigationContextToken, ctx)
}
